import { useState, type FormEvent } from 'react';
import { useLookups, todayLocal, normalizeUnitLabel } from '../lib/lookups';
import {
  useCreateUsageBatch,
  emptyUsageLine,
  filledUsageCount,
  validateUsage,
  usageHasErrors,
  type UsageDraft,
  type UsageLine,
  type UsageErrors,
} from '../lib/usage-batches';
import { Drawer, Field, useToast, Select } from './ui';
import { Box, Activity, Plus, Trash2, Calendar, Sparkles } from 'lucide-react';

/**
 * Record site material consumption across multiple materials for a specific activity.
 */
export default function UsageDrawer({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const { data: lk, isLoading } = useLookups();
  const create = useCreateUsageBatch(projectId);
  const toast = useToast();

  const [draft, setDraft] = useState<UsageDraft>({
    date: todayLocal(),
    activity: '',
    remarks: '',
    lines: [emptyUsageLine()],
  });
  const [errors, setErrors] = useState<UsageErrors>({ lines: {} });
  const [touched, setTouched] = useState(false);

  const set = <K extends keyof UsageDraft>(k: K, v: UsageDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const patchLine = (i: number, patch: Partial<UsageLine>) => {
    const next: UsageDraft = {
      ...draft,
      lines: draft.lines.map((l, n) => (n === i ? { ...l, ...patch } : l)),
    };
    setDraft(next);
    if (touched) setErrors(validateUsage(next));
  };

  const addLine = () =>
    setDraft((d) => ({ ...d, lines: [...d.lines, emptyUsageLine()] }));

  const removeLine = (i: number) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.length > 1 ? d.lines.filter((_, n) => n !== i) : [emptyUsageLine()],
    }));

  const count = filledUsageCount(draft.lines);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    const found = validateUsage(draft);
    setErrors(found);
    if (usageHasErrors(found)) return;

    try {
      await create.mutateAsync(draft);
      toast.show(`Recorded — ${count} material${count === 1 ? '' : 's'} used`);
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the entry', true);
    }
  }

  return (
    <Drawer
      title="Record Material Consumption"
      description="Log site stock used for a specific construction activity or milestone"
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Save Usage Entry"
      busy={create.isPending}
      wide
    >
      <div className="space-y-4">
        {/* Top Header Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50/70 p-3.5 sm:p-4 rounded-[10px] border border-slate-200/80">
          <Field label="Usage Date" error={errors.date}>
            <input
              type="date"
              value={draft.date}
              onChange={(e) => set('date', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>

          <Field label="Work Activity / Location" optional hint="Applied to all materials below">
            <input
              value={draft.activity}
              maxLength={120}
              onChange={(e) => set('activity', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>
        </div>

        {/* Multi-Row Material Consumption Table */}
        <div className="bg-white rounded-[10px] border border-slate-200/80 p-3.5 sm:p-4 space-y-3 shadow-xs">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-slate-800 m-0 flex items-center gap-1.5">
                <Box className="w-4 h-4 text-emerald-600" />
                Materials Consumed
              </h4>
              <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                Select materials and quantities deducted from site inventory stock.
              </p>
            </div>
            {errors.form && (
              <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2.5 py-0.5 rounded-[6px] border border-rose-200">
                {errors.form}
              </span>
            )}
          </div>

          <div className="bill-panel p-0 bg-transparent border-0">
            <div className="usage-line-head hidden sm:grid text-[11px] text-slate-400 font-bold uppercase tracking-wider pb-2 border-b border-slate-200/80">
              <div>Material Item</div>
              <div className="text-center">Qty Used</div>
              <div className="text-center">Unit</div>
              <div>Line Note / Activity Detail</div>
              <div />
            </div>

            <div className="bill-lines space-y-2 pt-2">
              {draft.lines.map((line, i) => {
                const le = errors.lines[i] ?? {};
                return (
                  <div
                    key={i}
                    className="usage-line items-center gap-2 bg-slate-50/50 p-2 rounded-[8px] border border-slate-200/60 hover:bg-slate-50 transition-colors"
                  >
                    <div className="lineno text-xs font-bold text-slate-400">#{i + 1}</div>

                    {/* Material Selector */}
                    <div className="field material">
                      <Select
                        size="sm"
                        value={line.material_id}
                        disabled={isLoading}
                        placeholder={isLoading ? 'Loading…' : 'Choose a material'}
                        options={[
                          { value: '', label: isLoading ? 'Loading…' : 'Choose a material' },
                          ...(lk?.materials?.map((m) => ({ value: m.id, label: m.name })) ?? []),
                        ]}
                        className={le.material_id ? 'border-rose-500' : ''}
                        onChange={(matId) => {
                          const mat = lk?.materials?.find((m) => m.id === matId);
                          patchLine(i, {
                            material_id: matId,
                            unit_id: line.unit_id || mat?.unit_id || '',
                          });
                        }}
                      />
                      {le.material_id && <div className="text-[10px] text-rose-600 mt-0.5">{le.material_id}</div>}
                    </div>

                    {/* Quantity */}
                    <div className="field qty">
                      <input
                        id={`uq-${i}`}
                        value={line.quantity}
                        inputMode="decimal"
                        aria-invalid={!!le.quantity}
                        onChange={(e) => patchLine(i, { quantity: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs text-center bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 font-mono font-semibold"
                      />
                      {le.quantity && <div className="text-[10px] text-rose-600 mt-0.5">{le.quantity}</div>}
                    </div>

                    {/* Unit */}
                    <div className="field unit">
                      <Select
                        size="sm"
                        value={line.unit_id}
                        disabled={isLoading}
                        placeholder="—"
                        options={[
                          { value: '', label: '—' },
                          ...(lk?.units?.map((u) => ({ value: u.id, label: normalizeUnitLabel(u.name, u.short_name) })) ?? []),
                        ]}
                        onChange={(val) => patchLine(i, { unit_id: val })}
                      />
                    </div>

                    {/* Line Remarks */}
                    <div className="field note">
                      <input
                        id={`un-${i}`}
                        value={line.remarks}
                        maxLength={120}
                        onChange={(e) => patchLine(i, { remarks: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800"
                      />
                    </div>

                    {/* Remove Line */}
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      title="Remove this line"
                      aria-label={`Remove line ${i + 1}`}
                      className="btn-remove-line rm"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={addLine}
              className="btn-add-line"
            >
              <Plus size={14} strokeWidth={2.2} />
              <span>Add Another Material Consumed</span>
            </button>
          </div>
        </div>

        {/* Totals Summary Banner */}
        <div className="drawer-summary-banner">
          <span className="title">
            {count} material{count === 1 ? '' : 's'} entered for consumption
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-faint)', fontStyle: 'italic' }}>
            {draft.activity.trim() ? `Activity: ${draft.activity}` : 'General Site Consumption'}
          </span>
        </div>

        {/* General Remarks */}
        <div>
          <Field label="Consumption Remarks" optional span2>
            <textarea
              rows={2}
              value={draft.remarks}
              maxLength={300}
              onChange={(e) => set('remarks', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>
        </div>
      </div>
    </Drawer>
  );
}
